import { useEffect, useState } from 'react'
import {
  requiredLangs,
  withLessonResult,
  type BlockIssue,
} from '@/features/editor/lib/publishIssues'
import type { CourseWithoutBlocks } from '@/types/course'
import { useHasStartedTranslationRun } from '../studio/useBlocksInRun'
import { LessonIssuesProbe } from '../EditorHeader/LessonIssuesProbe'

export interface ClientIssuesState {
  /** Урок без итога («не знаем»: не мерили или меряем заново) или зонды ещё не запущены. */
  pending: boolean
  byLesson: Record<number, BlockIssue[]>
}

/** Замечания, которые api не проверит (переполнение), — по порядку уроков. */
export const clientOnlyIssues = (
  course: CourseWithoutBlocks,
  byLesson: Record<number, BlockIssue[]>,
): BlockIssue[] =>
  (course.lessons ?? []).flatMap((l) => (byLesson[l.id] ?? []).filter((i) => i.clientOnly))

/**
 * Безголовый гейт (приём `EmptyVideoGate`): монтирует зонды экранов по урокам
 * и докладывает итоги наверх. Один на панель «Доступ» и на окно публикации —
 * агрегацию не дублируем. `ready=false` — зонды не запускаем, итог «проверяется».
 */
export const ClientIssuesGate = ({
  course,
  ready,
  onChange,
}: {
  course: CourseWithoutBlocks
  ready: boolean
  onChange: (state: ClientIssuesState) => void
}) => {
  const startedRun = useHasStartedTranslationRun(course.id)
  const [byLesson, setByLesson] = useState<Record<number, BlockIssue[]>>({})
  const lessons = course.lessons ?? []
  const pending = !ready || lessons.some((l) => byLesson[l.id] === undefined)
  useEffect(() => {
    onChange({ pending, byLesson })
    // onChange — родительский сеттер, состав итога целиком в pending и byLesson
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, byLesson])
  if (!ready) return null
  return (
    <>
      {lessons.map((lesson, rank) => (
        <LessonIssuesProbe
          key={lesson.id}
          lesson={lesson}
          rank={rank}
          langs={requiredLangs(course, startedRun)}
          onResult={(id, list) => setByLesson((prev) => withLessonResult(prev, id, list))}
        />
      ))}
    </>
  )
}
